/**
 * @fileOverview Implements a fetch function that can handle multipart/mixed responses.
 * The multipart response is expected to follow the GraphQL multipart request spec.
 *
 * See https://github.com/graphql/graphql-over-http/blob/main/rfcs/IncrementalDelivery.md#content-type-multipartmixed
 * See https://github.com/graphql/graphql-over-http/blob/main/spec/GraphQLOverHTTP.md#multipart-request-format
 */
import merge from 'lodash/merge';
import {
	type CacheConfig,
	Observable,
	type RequestParameters,
	type Variables,
} from 'relay-runtime';
import type { Sink } from 'relay-runtime/lib/network/RelayObservable';

import { fg } from '@atlaskit/platform-feature-flags';
import { getRelayExperimentalApis } from '@atlassian/relay-experimental-apis';

import type { GraphQLMultipartResponsePayload } from './types';

const NO_CONTENT = 204;

function getBoundary(contentType: string): string | null {
	const parts = contentType.split('boundary=');
	if (parts.length < 2) {
		return null;
	}
	return parts[1].trim();
}

function processChunk(
	chunk: string,
	boundaryString: string,
	observer: Sink<GraphQLMultipartResponsePayload>,
): string {
	const parts = chunk.split(boundaryString);
	let incompleteChunk = '';

	// Process all complete parts (except the last one which might be incomplete)
	for (let i = 0; i < parts.length - 1; i++) {
		const part = parts[i].trim();

		// Skip empty parts and encapsulation boundaries
		if (part && !part.startsWith('--')) {
			// Split content-type header and body
			const [part1, part2] = part.split(/\r?\n\r?\n/);

			// when response content has boundary string, part1 is the actual response
			// when response content does not have boundary string, part2 is the actual response
			const body = part2 ?? part1;

			if (body) {
				try {
					const jsonData = JSON.parse(incompleteChunk + body);
					observer.next(jsonData);
					incompleteChunk = '';
				} catch {
					// add boundary string back to response for cases when response content has boundary string
					// no error is thrown because next part might be a valid JSON object
					incompleteChunk += body + boundaryString;
				}
			}
		}
	}

	// Return the last part which might be incomplete
	return incompleteChunk + parts[parts.length - 1];
}

export async function processStream(
	reader: AsyncGenerator<string, void>,
	boundary: string,
	observer: Sink<GraphQLMultipartResponsePayload>,
): Promise<void> {
	const boundaryString = `--${boundary}`;
	let incompleteChunk = '';

	// Process chunks until the stream is done
	for await (const newText of reader) {
		const combinedChunk = incompleteChunk + newText;

		// Process the combined chunk and get any incomplete part
		incompleteChunk = processChunk(combinedChunk, boundaryString, observer);
	}

	// Process any remaining data
	if (incompleteChunk.length > 0) {
		processChunk(incompleteChunk, boundaryString, observer);
	}
	observer.complete();
}

function handleMultipartResponse(
	response: Response,
	observer: Sink<GraphQLMultipartResponsePayload>,
): void {
	const contentType = response.headers.get('content-type');
	const boundary = getBoundary(contentType || '');

	if (!boundary) {
		observer.error(new Error('Invalid multipart boundary'));
		return;
	}

	const decoder = new TextDecoder('utf-8');

	async function* streamReader() {
		// @ts-expect-error a Response object in Node is streamable
		for await (const chunk of response.body) {
			const newText = decoder.decode(chunk, { stream: true });
			yield newText;
		}
	}

	try {
		// Depending on the version of Node we're running in, the Response object may not implement ReadableStream.
		// Here, we treat the response as an async iterator, since both node-fetch and browser implement the response body as a stream.
		// We wrap that iteration in an async generator, which lets us
		processStream(streamReader(), boundary, observer).catch((error) => observer.error(error));
	} catch (e) {
		const error = new Error('Failed to read from response body');
		// @ts-ignore: cause property may not be available in all environments
		error.cause = e;
		observer.error(error);
	}
}

function handleJsonResponse(
	response: Response,
	observer: Sink<GraphQLMultipartResponsePayload>,
): void {
	response
		.json()
		.then((data) => observer.next(data))
		.catch((error) => observer.error(error))
		.finally(() => observer.complete());
}

function handleTextResponse(
	response: Response,
	observer: Sink<GraphQLMultipartResponsePayload>,
): void {
	response
		.text()
		.then((data) => {
			const maybeJson = data ? JSON.parse(data) : null;
			observer.next(maybeJson);
			observer.complete();
		})
		.catch((error) => {
			observer.error(
				new Error(
					`Error while parsing text response (Content-Type: ${response?.headers?.get(
						'content-type',
					)}): ${error instanceof Error ? error.message : String(error)}`,
				),
			);
		});
}

function processMultipartPromise(
	source: () => Promise<Response>,
): Observable<GraphQLMultipartResponsePayload> {
	// Cache the result of the source promise to avoid calling it multiple times.
	const result = source();
	// Wrap an observer around the result to handle the response.
	return Observable.create((observer) => {
		result
			.then((response) => {
				if (response.status === NO_CONTENT) {
					observer.complete();
					return;
				}

				const contentType = response.headers.get('content-type') || '';

				if (
					contentType.includes('application/json') ||
					contentType.includes('application/graphql-response+json')
				) {
					handleJsonResponse(response, observer);
				} else if (contentType.includes('text/plain')) {
					handleTextResponse(response, observer);
				} else if (contentType.includes('multipart/mixed')) {
					handleMultipartResponse(response, observer);
				} else {
					handleTextResponse(response, observer);
				}
			})
			.catch((error) => observer.error(error));
	});
}

const relayDeferAcceptableFormats = [
	'application/graphql-response+json',
	'application/json',
	// there must not be spacing between `multipart/mixed` and `deferSpec=20220824` since the backend assumes no spacing
	'multipart/mixed;deferSpec=20220824',
];

const MAX_QUERY_LENGTH = 2000; // Maximum allowed query length
const MAX_HEADERS_SIZE = 20 * 1024; // Maximum header size in bytes (20 KB)

// Calculate the size of the headers
function calculateHeadersSize(headers: HeadersInit): number {
	let totalBytes = 0;
	const textEncoder = new TextEncoder();

	// Iterate over each header entry (name and value)
	for (const [name, value] of Object.entries(headers)) {
		const headerSize = textEncoder.encode(`${name}: ${value}\r\n`).length; // Include CRLF
		totalBytes += headerSize;
	}

	return totalBytes;
}

function buildGetUrl(baseUrl: string, variables: Variables): string {
	const params = new URLSearchParams();
	params.append('variables', JSON.stringify(variables));

	return baseUrl + `${baseUrl.includes('?') ? '&' : '?'}${params.toString()}`;
}

function shouldUseGetMethod({
	url: baseUrl,
	variables,
	request,
	headers,
	isServer,
}: {
	url: string;
	variables: Variables;
	request: RequestParameters;
	headers?: HeadersInit;
	isServer: boolean;
}): { isGetRequest: boolean; reason?: string } {
	if (isServer) {
		return { isGetRequest: false, reason: 'SSR' };
	}

	if (request.operationKind !== 'query') {
		return { isGetRequest: false, reason: request.operationKind };
	}

	let queryLength = 0;

	if (fg('calculate_url_length_correctly_for_georgian_lang')) {
		queryLength = buildGetUrl(baseUrl, variables).length;
	} else {
		const serializedVariables = JSON.stringify(variables);
		queryLength = baseUrl.length + serializedVariables.length;
	}

	const headersSize = calculateHeadersSize(headers || {});
	return queryLength <= MAX_QUERY_LENGTH && headersSize <= MAX_HEADERS_SIZE
		? { isGetRequest: true }
		: { isGetRequest: false, reason: 'URL length or headers size too large' };
}

type BuildGraphqlSmartFetchParams = {
	url: string;
	options?: RequestInit;
	variables: Variables;
	request: RequestParameters;
	isServer: boolean;
	cacheConfig: CacheConfig;
};

function buildGraphqlSmartFetch({
	url,
	options,
	variables,
	request,
	isServer,
	cacheConfig,
}: BuildGraphqlSmartFetchParams): () => Promise<Response> {
	const headers: Record<string, string> = {
		'atl-client-name': 'atlassian-frontend-monorepo',
		Accept: relayDeferAcceptableFormats.join(', '),
		'X-ExperimentalApi': getRelayExperimentalApis().join(','),
		...(cacheConfig?.metadata?.headers || {}),
	};

	const requestOptions: RequestInit & { headers: Record<string, string> } = merge(options, {
		credentials: 'same-origin',
		headers,
	});

	requestOptions.headers['Content-Type'] = 'application/json';

	// Default `atl-attribution` header value for AGG requests
	// https://hello.atlassian.net/wiki/spaces/AGG/pages/5954141702/Metrics+Attribution+Onboarding+Doc+for+AGG+Clients
	if (!requestOptions.headers['atl-attribution']) {
		requestOptions.headers['atl-attribution'] = JSON.stringify({
			service: 'afm-frontend',
		});
	}

	const { isGetRequest, reason } = shouldUseGetMethod({
		url,
		variables,
		request,
		headers: requestOptions.headers,
		isServer,
	});

	// Nav4ProjectsContentQuery is forced to go POST route to avoid the stale cache issues when transition between issues in Issue-view
	// that's not a permanent fix, but rather an attampt to unblock the team
	// Context: #jiv-navigation-issue channel
	if (isGetRequest && request.name !== 'Nav4ProjectsContentQuery') {
		requestOptions.method = 'GET';

		url = buildGetUrl(url, variables);

		delete requestOptions.body; // Not needed for GET
	} else {
		requestOptions.method = 'POST';
		if (reason) {
			requestOptions.headers['X-Request-Fallback-To-Post-Reason'] = reason;
		}
	}

	return () => fetch(url, requestOptions);
}

export {
	buildGraphqlSmartFetch,
	processChunk,

	// These are only exported for testing purposes.
	processMultipartPromise,
	calculateHeadersSize,
	shouldUseGetMethod,
	buildGetUrl,
};
